import type { SearchEngine } from '../credentials';
import type { SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { inList } from '../predicate';
import { notTemplate } from './scopeFilters';

export type ProductionRunSearchStatus = 'DRAFT' | 'IN_PROGRESS' | 'COMPLETE';

interface ProductionRunLabel {
  readonly label: string;
  readonly color?: string | null;
}

/** A production run hit from either engine: the Typesense document, plus the Algolia-record fields the Algolia projection names. */
export interface ProductionRunDoc {
  readonly id: string;
  readonly sourceId: number;
  readonly organizationId: number;
  readonly rankScore: number;
  readonly reference: string;
  readonly status: ProductionRunSearchStatus;
  readonly componentsText: string;
  readonly notesText: string;
  readonly slug?: string;
  readonly slugTokens?: readonly string[];
  readonly commodity?: string;
  readonly labelNames?: readonly string[];
  readonly growerNames?: readonly string[];
  readonly locationNames?: readonly string[];
  readonly fieldTicketIds?: readonly string[];
  readonly vendors?: readonly string[];
  readonly vendorSos?: readonly string[];
  readonly slugs?: readonly string[];
  readonly labels?: readonly ProductionRunLabel[];
  readonly growers?: readonly string[];
  readonly harvestLocations?: readonly string[];
  /** Algolia records only: the Typesense projection drops template runs. */
  readonly isTemplate?: boolean;
}

export const PRODUCTION_RUN_DESCRIPTOR: SearchDescriptor<ProductionRunDoc> = {
  entity: 'productionRun',
  indexName: { algolia: 'productionRuns', typesense: 'productionRuns' },
  searchable: [
    'slugs',
    'labels',
    'commodity',
    'fieldTicketIds',
    'growers',
    'harvestLocations',
    'vendorSos',
    'vendors',
    'status',
  ],
  filterable: ['isTemplate', 'organizationId', 'status'],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [
      { path: ['slugs'] },
      { path: ['labels', 'label'], mode: 'unordered' },
      { path: ['commodity'], mode: 'unordered' },
      { path: ['fieldTicketIds'], mode: 'unordered' },
      { path: ['growers'], mode: 'unordered' },
      { path: ['harvestLocations'], mode: 'unordered' },
      { path: ['vendorSos'], mode: 'unordered' },
      { path: ['vendors'], mode: 'unordered' },
      { path: ['status'], mode: 'unordered' },
    ],
    attributesForFaceting: [
      { path: ['isTemplate'], mode: 'searchable' },
      { path: ['organizationId'], mode: 'searchable' },
      { path: ['status'], mode: 'searchable' },
    ],
  },
  ranking: {
    typesense: {
      queryBy:
        'reference,slugTokens,labelNames,vendors,vendorSos,fieldTicketIds,growerNames,locationNames,commodity,componentsText,notesText',
      sortBy: 'rankScore:desc',
    },
  },
  tenantField: 'organizationId',
};

export const PRODUCTION_RUN_WITH_STATUS = (
  statuses: readonly ProductionRunSearchStatus[]
): Predicate<ProductionRunDoc> => inList<ProductionRunDoc>('status', statuses);

export const PRODUCTION_RUN_NOT_TEMPLATE = (engine: SearchEngine): Predicate<ProductionRunDoc> =>
  notTemplate<ProductionRunDoc>('isTemplate', engine);
